import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { UnifiedAvatar } from '@/components/shared/UnifiedAvatar';
import { useTicketUserInfo } from '@/hooks/useTicketUserInfo';
import { getPlanCommercialName } from '@/constants/plans';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  User, 
  MapPin, 
  Globe, 
  Monitor, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  ExternalLink,
  Shield,
  Loader2
} from 'lucide-react';

interface TicketUserInfoCardProps {
  userId: string;
  isStaff: boolean;
}

const parseUserAgent = (ua: string | null): string => {
  if (!ua) return 'Desconhecido';
  
  // Parse browser
  const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/(\d+)/);
  const browser = browserMatch ? `${browserMatch[1]} ${browserMatch[2]}` : 'Browser';
  
  // Parse OS
  let os = 'Outro';
  if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return `${browser} · ${os}`;
};

const getRoleBadgeVariant = (role: string | null) => {
  switch (role) {
    case 'sponsor': return 'default';
    case 'sponsored': return 'secondary';
    default: return 'outline';
  }
};

const getRoleLabel = (role: string | null) => {
  switch (role) {
    case 'sponsor': return 'Sponsor';
    case 'sponsored': return 'Sponsored';
    default: return role || 'N/A';
  }
};

export const TicketUserInfoCard = ({ userId, isStaff }: TicketUserInfoCardProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const { data: userInfo, isLoading } = useTicketUserInfo(userId, isStaff);

  if (!isStaff) return null;

  if (isLoading) {
    return (
      <Card className="bg-amber-500/5 border-amber-500/20 mb-6">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-2 text-amber-400">
            <User className="w-4 h-4" />
            <span className="text-sm font-medium">Informações do Usuário</span>
            <Loader2 className="w-4 h-4 animate-spin ml-auto" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (!userInfo) return null;

  const planName = getPlanCommercialName(userInfo.plan_type);
  const isVerified = userInfo.verification_level === 'full' || userInfo.verification_level === 'verified';
  const location = [userInfo.city, userInfo.state_code].filter(Boolean).join(', ') || 'Não informado';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="bg-amber-500/5 border-amber-500/20 mb-6">
        <CollapsibleTrigger asChild>
          <CardHeader className="py-3 px-4 cursor-pointer hover:bg-white/5 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-400">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Informações do Usuário</span>
              </div>
              {isOpen ? (
                <ChevronUp className="w-4 h-4 text-white/40" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white/40" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 px-4 space-y-4">
            {/* Avatar + Name + Badges */}
            <div className="flex items-center gap-4">
              <UnifiedAvatar 
                src={userInfo.main_photo_url} 
                alt={userInfo.username || 'User'} 
                size="medium"
                fallback={userInfo.username?.charAt(0).toUpperCase() || 'U'}
                showOnlineIndicator={userInfo.is_online || false}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white truncate">
                    {userInfo.username || 'Usuário'}
                  </span>
                  {isVerified && (
                    <Shield className="w-4 h-4 text-emerald-400" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant={getRoleBadgeVariant(userInfo.role)} className="text-xs">
                    {getRoleLabel(userInfo.role)}
                  </Badge>
                  <Badge variant="outline" className="text-xs border-luxury-pinkBright/30 text-luxury-pinkBright">
                    {planName}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Location + IP */}
            <div className="flex items-center gap-4 text-sm text-white/60 flex-wrap">
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-white/40" />
                <span>{location}</span>
              </div>
              {userInfo.client_ip && (
                <div className="flex items-center gap-1.5">
                  <Globe className="w-4 h-4 text-white/40" />
                  <span className="font-mono text-xs">{userInfo.client_ip}</span>
                </div>
              )}
            </div>

            {/* Device */}
            {userInfo.user_agent && (
              <div className="flex items-center gap-1.5 text-sm text-white/60">
                <Monitor className="w-4 h-4 text-white/40" />
                <span>{parseUserAgent(userInfo.user_agent)}</span>
              </div>
            )}

            {/* Dates */}
            <div className="flex items-center gap-4 text-sm text-white/60 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-white/40" />
                <span>
                  Conta: {userInfo.created_at 
                    ? format(new Date(userInfo.created_at), 'dd/MM/yyyy', { locale: ptBR })
                    : 'N/A'}
                </span>
              </div>
              {userInfo.last_login && (
                <span className="text-white/40">·</span>
              )}
              {userInfo.last_login && (
                <span>
                  Login: {formatDistanceToNow(new Date(userInfo.last_login), { 
                    addSuffix: true, 
                    locale: ptBR 
                  })}
                </span>
              )}
            </div>

            {/* Link to profile */}
            {userInfo.username && (
              <Button 
                variant="ghost" 
                size="sm" 
                asChild 
                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 -ml-2"
              >
                <Link to={`/main/user/${userInfo.username}`}>
                  Ver perfil completo
                  <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </Link>
              </Button>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
